/**
 * Product Feed Collection Switcher
 *
 * Intercepts clicks on collection-linked buttons within product feed sections
 * and replaces the product list via section rendering instead of navigating away.
 */
class ProductFeedCollectionSwitcher {
  constructor() {
    this.loadingStates = new WeakMap();
    this.abortControllers = new WeakMap();
    this.htmlCache = new Map();
    this.collectionRegex = /^\/collections\/([^/?#]+)\/?$/;
    this.initialized = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.initButtonStates();
    document.addEventListener('click', this.handleClick);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
  }

  handleSectionLoad(event) {
    const section = event.target?.querySelector?.('[data-product-feed-collection-switcher="true"]')
      || event.target;
    if (!(section instanceof Element)) return;
    if (section.matches?.('[data-product-feed-collection-switcher="true"]')) {
      const handle = this.getActiveHandle(section);
      this.setActiveButton(section, handle);
      this.syncViewAllLinks(section, handle);
    }
  }

  parseCollectionLink(link) {
    try {
      const pathname = new URL(link.getAttribute('href'), window.location.origin).pathname;
      const match = pathname.match(this.collectionRegex);
      return match ? { path: pathname, handle: match[1] } : null;
    } catch (_) {
      return null;
    }
  }

  setActiveButton(section, activeHandle) {
    section.querySelectorAll('[id^="button-wrapper-"] a[href]').forEach((link) => {
      const parsed = this.parseCollectionLink(link);
      const isActive = !!(activeHandle && parsed?.handle === activeHandle);
      link.classList.toggle('is-active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  getActiveHandle(section) {
    return section.querySelector('.block-product-list')?.dataset.activeCollectionHandle
      || section.dataset.defaultCollectionHandle
      || null;
  }

  syncViewAllLinks(section, handle) {
    const links = section.querySelectorAll('a[data-product-feed-view-all]');
    const href = handle ? `/collections/${handle}` : '/';
    links.forEach((link) => {
      link.setAttribute('href', href);
    });
  }

  setLoadingState(section, isLoading) {
    const feed = section.querySelector('.block-product-list');
    if (!feed) return;

    const cards = feed.querySelectorAll('.slider-content > *');

    if (isLoading) {
      feed.setAttribute('aria-busy', 'true');
      feed.classList.add('is-loading');
      cards.forEach((card) => {
        card.style.transition = 'opacity 0.2s ease';
        card.style.opacity = '0.45';
      });
    } else {
      feed.removeAttribute('aria-busy');
      feed.classList.remove('is-loading');
      cards.forEach((card) => {
        card.style.opacity = '';
      });
    }
  }

  reinitializeMedia() {
    if (window.ThemeBus && window.EVENTS?.gridUpdated) {
      window.ThemeBus.dispatchEvent(new Event(window.EVENTS.gridUpdated));
      return;
    }

    if (typeof window.LazyImageLoader === 'function') {
      window.__lazyImageLoaderActive = false;
      new window.LazyImageLoader();
    }
  }

  async refreshFeed(section, collectionPath) {
    const sectionId = section.dataset.sectionId;
    if (!sectionId) return;

    // Abort any in-flight request for this section
    this.abortControllers.get(section)?.abort();
    const controller = new AbortController();
    this.abortControllers.set(section, controller);

    // Use cached response if available, otherwise fetch fresh
    const cacheKey = `${sectionId}:${collectionPath}`;
    let html = this.htmlCache.get(cacheKey);

    if (!html) {
      const url = new URL(collectionPath, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!res.ok) throw new Error('Failed to refresh product feed');

      html = await res.text();
      this.htmlCache.set(cacheKey, html);
    }

    const nextFeed = new DOMParser().parseFromString(html, 'text/html').querySelector('.block-product-list');
    if (!nextFeed) throw new Error('Updated product feed markup not found');

    const currentFeed = section.querySelector('.block-product-list');
    if (!currentFeed) return;

    currentFeed.replaceWith(nextFeed);
    const newHandle = nextFeed.dataset.activeCollectionHandle || null;
    this.setActiveButton(section, newHandle);
    this.syncViewAllLinks(section, newHandle);
    this.reinitializeMedia();
    window.initSliderBlocks?.();
  }

  async handleClick(event) {
    const link = event.target.closest('a[href]');
    if (!link) return;

    if (link.hasAttribute('data-product-feed-view-all')) return;

    const section = link.closest('[data-product-feed-collection-switcher="true"]');
    if (!section) return;

    const parsed = this.parseCollectionLink(link);
    if (!parsed) return;

    event.preventDefault();

    // Skip if already loading or clicking the already-active collection
    if (this.loadingStates.get(section)) return;
    if (parsed.handle === this.getActiveHandle(section)) return;

    this.loadingStates.set(section, true);
    this.setLoadingState(section, true);

    try {
      await this.refreshFeed(section, parsed.path);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('[Product Feed] Collection switch failed', error);
      }
    } finally {
      this.loadingStates.set(section, false);
      this.setLoadingState(section, false);
    }
  }

  initButtonStates() {
    document.querySelectorAll('[data-product-feed-collection-switcher="true"]').forEach((section) => {
      const handle = this.getActiveHandle(section);
      this.setActiveButton(section, handle);
      this.syncViewAllLinks(section, handle);
    });
  }
}

const productFeedCollectionSwitcher = new ProductFeedCollectionSwitcher();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => productFeedCollectionSwitcher.init());
} else {
  productFeedCollectionSwitcher.init();
}

window.ProductFeedCollectionSwitcher = productFeedCollectionSwitcher;
